import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Wheat, ArrowLeft, Target, Building2, Lock, ScrollText, ShieldCheck } from "lucide-react";

const SECTIONS = [
  {
    icon: Target,
    title: "Purpose of This Platform",
    body: "This platform has been developed as an experimental environment for testing, utilizing, and refining new and enhanced training tools. These resources are designed to originate at the local store level with the potential to be scaled company-wide as needed. It serves as an ongoing coaching, communication, and educational resource for local cafe operations.",
  },
  {
    icon: Building2,
    title: "Affiliation and Ownership",
    body: "Please be advised that this site and platform are independent, third-party resources. They are not officially affiliated with, sponsored by, operated by, or endorsed by Panera Bread or its corporate entities. The platform was independently created and is maintained by James Clarke, a local cafe Team Lead and Manager in Charge (MIC) who is currently studying AI Engineering. This platform is hosted by James Clarke and serves as a functional, real-world project within his online professional portfolio to demonstrate scalable application development and deployment.",
  },
  {
    icon: Lock,
    title: "Privacy and Data Protection",
    body: "Protecting the privacy and data of all individuals and the company is our highest priority. All personal information, account details, feedback, and internal communications shared on this platform are strictly confidential. The platform has been architected with robust security measures and strict data handling protocols to safeguard individual user profiles and proprietary operational knowledge.\n\nData collected on this platform (such as names, phone numbers, and store locations) is used strictly for the administration, security, and core functionality of this training tool. User data, metrics, and internal company information will never be sold, distributed, or utilized for any external commercial purposes.",
  },
  {
    icon: ScrollText,
    title: "Liability and User Acknowledgement",
    body: "By registering for and utilizing this platform, users acknowledge that it is a locally managed, third-party training resource currently in a testing phase. While every effort is made to ensure the accuracy, security, and reliability of the platform, it is provided on an \"as-is\" basis. The creator assumes no liability for technical disruptions or data loss.\n\nFurthermore, while the content provided by the podcast hosts and article creators is designed to strictly align with current brand and safety standards, official Panera Bread corporate resources, published guidelines, and direct upper-management directives will always supersede any content, advice, or training found on this platform.",
  },
];

export default function Disclaimers() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-brand-page">
      <header className="max-w-3xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl bg-brand flex items-center justify-center"><Wheat className="w-5 h-5 text-white" /></div>
          <span className="font-display text-2xl text-brand-ink">The Spread</span>
        </Link>
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-brand-ink" data-testid="disclaimers-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
        </Button>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8" data-testid="disclaimers-page">
        <div className="flex items-center gap-2 text-brand mb-2">
          <ShieldCheck className="w-5 h-5" />
          <span className="text-xs uppercase tracking-widest font-semibold">Legal &amp; Privacy</span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-brand-ink">Platform Disclaimer &amp; Privacy Notice</h1>
        <p className="text-brand-muted mt-3 leading-relaxed">
          Please review the following important information about how The Spread operates, its independent status,
          and how your data is protected.
        </p>

        <div className="mt-8 space-y-5">
          {SECTIONS.map((s, i) => (
            <section key={i} className="rounded-2xl border border-brand-line bg-white p-6 sm:p-8 card-shadow" data-testid={`disclaimer-section-${i}`}>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                  <s.icon className="w-5 h-5 text-brand" />
                </div>
                <h2 className="font-display text-2xl text-brand-ink">{s.title}</h2>
              </div>
              <div className="mt-4 text-brand-ink/90 leading-relaxed whitespace-pre-line">{s.body}</div>
            </section>
          ))}
        </div>

        <div className="mt-8 rounded-2xl bg-brand-sidebar/60 border border-brand-line p-6 text-sm text-brand-muted">
          <p className="font-semibold text-brand-ink">Independently created &amp; maintained by James Clarke</p>
          <p className="mt-1">Team Lead &amp; Manager in Charge · AI Engineering student · Portfolio project</p>
          <p className="mt-3">© {new Date().getFullYear()} James Clarke. The Spread is not affiliated with, sponsored by, or endorsed by Panera Bread.</p>
        </div>
      </div>
    </div>
  );
}
